import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Leaf, Sparkles, ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = ({ hero }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

    const words = hero.title.split(" ");
    const first = words.slice(0, -1).join(" ");
    const last = words[words.length - 1];

    return (
        <section ref={ref} id="top" className="relative pt-16 sm:pt-24 pb-16">
            <motion.p
                data-testid="hero-eyebrow"
                className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-bold text-[#718370]"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
            >
                <span className="h-px w-10 bg-[#9AB098]" />
                {hero.eyebrow}
            </motion.p>

            <h1
                data-testid="hero-heading"
                className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1D1A] leading-[1.05]"
            >
                <MaskedLine delay={0.15}>{first}</MaskedLine>
                <MaskedLine delay={0.3}>
                    <em className="not-italic text-[#576856]">{last.split(" ")[0]}</em>
                    {last.includes(" ") ? ` ${last.split(" ").slice(1).join(" ")}` : ""}
                </MaskedLine>
            </h1>

            <motion.p
                data-testid="hero-subtitle"
                className="mt-6 text-base sm:text-lg leading-relaxed text-[#6D736D] max-w-[640px]"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            >
                {hero.subtitle}
            </motion.p>

            <motion.div
                className="relative mt-12"
                initial={{ opacity: 0, y: 40, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.7, ease: EASE }}
            >
                <motion.div
                    className="absolute -top-5 -left-3 sm:-left-6 z-10 text-[#718370]"
                    animate={{ y: [0, -9, 0], rotate: [0, -6, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                    <Leaf size={30} strokeWidth={1.5} />
                </motion.div>
                <motion.div
                    className="absolute -bottom-4 -right-3 sm:-right-5 z-10 text-[#B89052]"
                    animate={{ y: [0, 8, 0], rotate: [0, 8, 0] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                    <Sparkles size={26} strokeWidth={1.5} />
                </motion.div>

                <div className="spotlight relative overflow-hidden rounded-[20px] border border-[#E4DFD3] shadow-[0_24px_60px_-24px_rgba(34,37,34,0.25)]">
                    <motion.img
                        data-testid="hero-image"
                        src={hero.image}
                        alt="Fresh breath — a confident smile"
                        className="h-[300px] sm:h-[420px] w-full object-cover scale-110"
                        style={{ y: imgY }}
                        loading="eager"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#222522]/25 via-transparent to-transparent" />
                </div>
            </motion.div>

            <motion.div
                className="mt-10 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
            >
                <motion.div
                    animate={{ y: [0, 7, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E4DFD3] text-[#718370]"
                >
                    <ArrowDown size={16} strokeWidth={1.8} />
                </motion.div>
            </motion.div>
        </section>
    );
};
